import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styles/styled-components';
import messages from './messages';
import { commonDimensions } from 'utils/constants';

/* ─── Wrapper ─────────────────────────────────────────────────────────────── */
const Container = styled.div`
  width: 100%;
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto 24px;
`;
const Wrapper = styled.div`
  width: 100%;
  background: #1c2534;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 64px;
  box-sizing: border-box;
  border-radius: 16px;
  @media (max-width: 1023px) {
    padding: 32px 16px;
  }
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  @media (max-width: 1023px) {
    flex-direction: column;
    gap: 40px;
  }
`;

/* ─── Left ────────────────────────────────────────────────────────────────── */
const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;
  @media (max-width: 1023px) {
    gap: 24px;
    align-items: center;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 1023px) {
    align-items: center;
    text-align: center;
  }
`;

const Title = styled.h2`
  font-size: 30px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
  line-height: 1.3;
  @media (max-width: 1023px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #bfc3cb;
  margin: 0;
  line-height: 1.6;
  white-space: pre-line;
  max-width: 400px;
`;

const SubtitleHighlight = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #FFFAE9;
  margin: 0;
  line-height: 1.6;
  white-space: pre-line;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 1023px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #43a047;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  padding: 9px 16px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s;
  &:hover {
    background: #388e3c;
    color: #ffffff;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  padding: 9px 16px;
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  // border: 1px solid rgba(255, 255, 255, 0.2);
  transition: background 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
  }
`;

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.9165 7H11.0832M11.0832 7L7.58317 3.5M11.0832 7L7.58317 10.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ─── Right (glass card) ──────────────────────────────────────────────────── */
const RightCard = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 440px;
  @media (max-width: 1023px) {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CardHeaderRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const SparkleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <g clip-path="url(#clip0_7989_189423)">
      <path
        d="M7.45258 11.6252C7.38562 11.3656 7.25034 11.1288 7.0608 10.9392C6.87126 10.7497 6.63439 10.6144 6.37483 10.5474L1.77358 9.36094C1.69508 9.33866 1.62599 9.29138 1.57679 9.22627C1.52759 9.16117 1.50098 9.08179 1.50098 9.00019C1.50098 8.91858 1.52759 8.83921 1.57679 8.7741C1.62599 8.709 1.69508 8.66172 1.77358 8.63944L6.37483 7.45219C6.63429 7.38529 6.8711 7.25012 7.06063 7.06072C7.25017 6.87132 7.38551 6.6346 7.45258 6.37519L8.63908 1.77394C8.66114 1.69513 8.70837 1.62569 8.77357 1.57623C8.83878 1.52677 8.91837 1.5 9.00021 1.5C9.08205 1.5 9.16164 1.52677 9.22684 1.57623C9.29204 1.62569 9.33928 1.69513 9.36133 1.77394L10.5471 6.37519C10.614 6.63474 10.7493 6.87161 10.9389 7.06115C11.1284 7.25069 11.3653 7.38598 11.6248 7.45294L16.2261 8.63869C16.3052 8.66051 16.375 8.70769 16.4247 8.77299C16.4744 8.83829 16.5014 8.91811 16.5014 9.00019C16.5014 9.08227 16.4744 9.16208 16.4247 9.22738C16.375 9.29268 16.3052 9.33986 16.2261 9.36169L11.6248 10.5474C11.3653 10.6144 11.1284 10.7497 10.9389 10.9392C10.7493 11.1288 10.614 11.3656 10.5471 11.6252L9.36058 16.2264C9.33853 16.3052 9.2913 16.3747 9.22609 16.4241C9.16089 16.4736 9.0813 16.5004 8.99946 16.5004C8.91762 16.5004 8.83803 16.4736 8.77282 16.4241C8.70762 16.3747 8.66039 16.3052 8.63833 16.2264L7.45258 11.6252Z"
        stroke="white"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path d="M15 2.25V5.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M16.5 3.75H13.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M3 12.75V14.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M3.75 13.5H2.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_7989_189423">
        <rect width="18" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const CardTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #ffffff;
`;

const CardSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #bfc3cb;
`;

/* ─── Stats ───────────────────────────────────────────────────────────────── */
const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

const StatItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 16px;
`;

const StatValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.4;
  @media (max-width: 1023px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #bfc3cb;
  line-height: 1.5;
  text-align: center;
  @media (max-width: 1023px) {
    font-size: 10px;
    line-height: 14px;
  }
`;

const KamereoLPText = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #bfc3cb;
  @media (max-width: 1023px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const KamereoFreshText = styled(Link)`
  color: #b3df72;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    color: #b3df72;
    opacity: 0.9;
  }
`;
const KamereoEssentialText = styled(Link)`
  color: #ffce24;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    color: #ffce24;
    opacity: 0.9;
  }
`;

/* ─── Component ───────────────────────────────────────────────────────────── */
const SignupCtaBanner: FC = () => {
  const stats = [
    { value: '2,000+', labelMsg: messages.stat1Label },
    { value: '99%', labelMsg: messages.stat2Label },
    { value: '98%', labelMsg: messages.stat3Label },
  ];
  const lang = localStorage.getItem('lang') || 'vi';

  return (
    <Container className={'container-padding'}>
      <Wrapper>
        <Inner>
          {/* Left */}
          <LeftSection>
            <TextGroup>
              <Title>
                <FormattedMessage {...messages.title} />
              </Title>

              {lang === 'vi' ? (
                <>
                  <Subtitle>
                    Tạo tài khoản miễn phí để nhận ngay <SubtitleHighlight>set 10 món quà tặng</SubtitleHighlight> và{' '}
                    <SubtitleHighlight>mở khoá mức giá ưu đãi</SubtitleHighlight> dành cho doanh nghiệp.
                  </Subtitle>
                  <KamereoLPText>
                    Trải nghiệm <KamereoFreshText to="/kamereo-ngon">Kamereo Ngon</KamereoFreshText> &{' '}
                    <KamereoEssentialText to="/kamereo-tien-dung">Tiện dụng</KamereoEssentialText> — tiết kiệm đến 30%.
                  </KamereoLPText>
                </>
              ) : (
                <>
                  <Subtitle>
                    Create a free account to get{' '}
                    <SubtitleHighlight>a complimentary set of 10 welcome gifts</SubtitleHighlight> and{' '}
                    <SubtitleHighlight>unlock exclusive B2B pricing</SubtitleHighlight>.
                  </Subtitle>
                  <KamereoLPText>
                    Experience <KamereoFreshText to="/kamereo-ngon">Kamereo Fresh</KamereoFreshText> &{' '}
                    <KamereoEssentialText to="/kamereo-tien-dung">Essential</KamereoEssentialText> — save up to 30%.
                  </KamereoLPText>
                </>
              )}
            </TextGroup>
            <ButtonRow>
              <PrimaryButton to="/contact-us">
                <FormattedMessage {...messages.createFreeAccount} />
                <ArrowIcon />
              </PrimaryButton>
              <SecondaryButton to="/signin">
                <FormattedMessage {...messages.signIn} />
              </SecondaryButton>
            </ButtonRow>
          </LeftSection>

          {/* Right glass card */}
          <RightCard>
            <CardHeader>
              <SparkleIcon />
              <CardHeaderRow>
                <CardTitle>
                  <FormattedMessage {...messages.partneringWithKamereo} />
                </CardTitle>
                <CardSubtitle>
                  <FormattedMessage {...messages.partneringSubtitle} />
                </CardSubtitle>
              </CardHeaderRow>
            </CardHeader>
            <StatsRow>
              {stats.map((s) => (
                <StatItem key={s.labelMsg.id}>
                  <StatValue>{s.value}</StatValue>
                  <StatLabel>
                    <FormattedMessage {...s.labelMsg} />
                  </StatLabel>
                </StatItem>
              ))}
            </StatsRow>
          </RightCard>
        </Inner>
      </Wrapper>
    </Container>
  );
};

export default SignupCtaBanner;
